import type { ReactNode } from 'react';
import { Badge } from '@shared/ui';
import { cn } from '@shared/lib/cn';
import { LOCALES, type Category } from '@entities/taxonomy';
import { pickLocalized, type Locale } from '@shared/i18n';
import type { TreeNode } from './category-model';

export interface TreeActions {
  onAddChild: (parent: Category) => void;
  onEdit: (c: Category) => void;
  onDelete: (c: Category) => void;
  onMove: (c: Category, direction: -1 | 1, siblings: Category[]) => void;
}

export function CategoryTree({
  forest,
  locale,
  expanded,
  onToggle,
  actions,
  reorderingId,
  depth = 0,
}: {
  forest: TreeNode[];
  locale: Locale;
  expanded: Set<string>;
  onToggle: (id: string) => void;
  actions: TreeActions;
  reorderingId: string | null;
  depth?: number;
}) {
  const siblings = forest.map((n) => n.category);

  return (
    <ul className={cn(depth > 0 && 'ml-[19px] border-l border-border pl-2')}>
      {forest.map((node, index) => (
        <CategoryRow
          key={node.category.id}
          node={node}
          index={index}
          siblings={siblings}
          locale={locale}
          expanded={expanded}
          onToggle={onToggle}
          actions={actions}
          reorderingId={reorderingId}
          depth={depth}
        />
      ))}
    </ul>
  );
}

function CategoryRow({
  node,
  index,
  siblings,
  locale,
  expanded,
  onToggle,
  actions,
  reorderingId,
  depth,
}: {
  node: TreeNode;
  index: number;
  siblings: Category[];
  locale: Locale;
  expanded: Set<string>;
  onToggle: (id: string) => void;
  actions: TreeActions;
  reorderingId: string | null;
  depth: number;
}) {
  const { category, children } = node;
  const hasChildren = children.length > 0;
  const isOpen = expanded.has(category.id);
  const missing = LOCALES.filter((l) => !category.name?.[l]?.trim());
  const busy = reorderingId === category.id;

  return (
    <li>
      <div
        className={cn(
          'group flex items-center gap-2 rounded-md py-1.5 pr-2 transition-colors hover:bg-accent',
          busy && 'opacity-50',
        )}
      >
        {hasChildren ? (
          <button
            type="button"
            onClick={() => onToggle(category.id)}
            className="flex h-6 w-6 shrink-0 items-center justify-center rounded text-xs text-muted-foreground hover:bg-background"
            aria-label={isOpen ? 'Свернуть' : 'Раскрыть'}
            aria-expanded={isOpen}
          >
            {isOpen ? '▾' : '▸'}
          </button>
        ) : (
          <span className="h-6 w-6 shrink-0" />
        )}

        <div className="flex min-w-0 flex-1 items-center gap-2">
          <span className="truncate text-sm font-medium">
            {pickLocalized(category.name, locale, category.slug)}
          </span>
          <span className="shrink-0 font-mono text-[11px] text-muted-foreground">
            {category.slug}
          </span>
          {hasChildren ? (
            <Badge variant="secondary" className="shrink-0 text-[10px]">
              {children.length}
            </Badge>
          ) : null}
          {missing.length > 0 ? (
            <Badge
              variant="warning"
              className="shrink-0 text-[10px]"
              title={`Не заполнены локали: ${missing.join(', ')}`}
            >
              нет {missing.join('/')}
            </Badge>
          ) : null}
        </div>

        <div className="flex shrink-0 items-center opacity-40 transition-opacity group-hover:opacity-100 focus-within:opacity-100">
          <IconButton
            title="Выше"
            disabled={index === 0 || busy}
            onClick={() => actions.onMove(category, -1, siblings)}
          >
            ↑
          </IconButton>
          <IconButton
            title="Ниже"
            disabled={index === siblings.length - 1 || busy}
            onClick={() => actions.onMove(category, 1, siblings)}
          >
            ↓
          </IconButton>
          <span className="mx-1 h-4 w-px bg-border" />
          <IconButton title="Добавить подкатегорию" onClick={() => actions.onAddChild(category)}>
            ＋
          </IconButton>
          <IconButton title="Редактировать" onClick={() => actions.onEdit(category)}>
            ✎
          </IconButton>
          <IconButton
            title="Удалить"
            destructive
            onClick={() => actions.onDelete(category)}
          >
            ✕
          </IconButton>
        </div>
      </div>

      {hasChildren && isOpen ? (
        <CategoryTree
          forest={children}
          locale={locale}
          expanded={expanded}
          onToggle={onToggle}
          actions={actions}
          reorderingId={reorderingId}
          depth={depth + 1}
        />
      ) : null}
    </li>
  );
}

function IconButton({
  title,
  onClick,
  disabled,
  destructive,
  children,
}: {
  title: string;
  onClick: () => void;
  disabled?: boolean;
  destructive?: boolean;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      title={title}
      aria-label={title}
      disabled={disabled}
      onClick={onClick}
      className={cn(
        'flex h-7 w-7 items-center justify-center rounded text-xs transition-colors',
        'text-muted-foreground hover:bg-background hover:text-foreground',
        destructive && 'hover:text-destructive',
        disabled && 'pointer-events-none opacity-25',
      )}
    >
      {children}
    </button>
  );
}
